import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import getBonusMetrics, {
  BonusMetricAssignment,
} from 'screens/Settings/Advanced/ManageBonusMetrics/api/getBonusMetrics';

export type BonusProgramAssignments = {
  program: BonusTypes.BonusProgram;
  metrics: BonusMetricAssignment[];
};

interface BonusProgramMetricAssignmentsContextType {
  programAssignments: BonusProgramAssignments[];
  isLoading: boolean;
  refreshAssignments: () => void;
}

export const BonusProgramMetricAssignmentsContext =
  React.createContext<BonusProgramMetricAssignmentsContextType>({
    programAssignments: [],
    isLoading: false,
    refreshAssignments: () => {
      const error = new Error();
      error.name = 'BonusProgramMetricAssignments: Function is undefined';
      throw error;
    },
  });

/**
 * Bonus program metric assignments live server-side (fetched per program via
 * `/v1/driver_bonus/metrics`), not in Firestore content. They are loaded once
 * per account so the metric popup's bonus lock and the Data Manager usage
 * report have them to hand, rather than each fetching (and waiting) on open.
 * Makes no requests for wallboard users (they never edit metrics) or accounts
 * without bonus programs. Programs whose fetch fails are skipped.
 */
const BonusProgramMetricAssignmentsProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { isWallboardUser } = useContext(CurrentUserContext);
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);

  // bonusPrograms gets a new identity on every Firestore update, so only
  // refetch when the set of programs actually changes
  const programIdsKey = useMemo(
    () =>
      bonusPrograms
        .map((p) => p.id)
        .sort()
        .join(','),
    [bonusPrograms],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const programs = useMemo(() => bonusPrograms, [programIdsKey]);
  const shouldFetch = !isWallboardUser && programs.length > 0;

  const [programAssignments, setProgramAssignments] = useState<
    BonusProgramAssignments[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(shouldFetch);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const refreshAssignments = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let isActive = true;

    if (!shouldFetch) {
      setProgramAssignments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    Promise.all(
      programs.map((program) =>
        getBonusMetrics({ api, driverBonusProgramId: program.id }).then(
          (response) => ({ program, response }),
        ),
      ),
    ).then((results) => {
      if (!isActive) {
        return;
      }

      const newAssignments: BonusProgramAssignments[] = [];
      results.forEach(({ program, response }) => {
        if (!response.ok || !response.data) {
          return;
        }

        newAssignments.push({ program, metrics: response.data.metrics });
      });

      setProgramAssignments(newAssignments);
      setIsLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [api, programs, refreshKey, shouldFetch]);

  const value = useMemo(
    () => ({ programAssignments, isLoading, refreshAssignments }),
    [isLoading, programAssignments, refreshAssignments],
  );

  return (
    <BonusProgramMetricAssignmentsContext.Provider value={value}>
      {children}
    </BonusProgramMetricAssignmentsContext.Provider>
  );
};

export default BonusProgramMetricAssignmentsProvider;
